<script setup lang="ts">
/**
 * ChangeRoleModal — 1:1 порт docs/mockups/modal-change-role.html (inbox-183).
 *
 * Size sm. User-card + radio role-grid (viewer/member/admin/owner).
 * Current роль помечена «текущая» chip. Transfer ownership — отдельный
 * confirm-flow (только Owner может).
 *
 * Payload: { userId, userName, userEmail, userInitials, currentRole, joinedAt?, linksCount?, campaignsCount? }
 * Emit: 'change-role' { userId, newRole }
 */
import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiBadge from '@/components/ui/UiBadge.vue';

const modals = useModalStore();

type Role = 'viewer' | 'member' | 'admin' | 'owner';
interface Payload {
  userId: string | number;
  userName: string;
  userEmail: string;
  userInitials: string;
  currentRole: Role;
  joinedAt?: string;
  linksCount?: number;
  campaignsCount?: number;
}
const payload = computed<Payload>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  const cr = typeof raw.currentRole === 'string' ? raw.currentRole as Role : 'member';
  return {
    userId: (raw.userId as string | number) ?? 0,
    userName: typeof raw.userName === 'string' ? raw.userName : 'Мария Соколова',
    userEmail: typeof raw.userEmail === 'string' ? raw.userEmail : 'm.sokolova@alfa.ru',
    userInitials: typeof raw.userInitials === 'string' ? raw.userInitials : 'МС',
    currentRole: cr,
    joinedAt: typeof raw.joinedAt === 'string' ? raw.joinedAt : '14 апреля 2026',
    linksCount: typeof raw.linksCount === 'number' ? raw.linksCount : 47,
    campaignsCount: typeof raw.campaignsCount === 'number' ? raw.campaignsCount : 12,
  };
});

const newRole = ref<Role>(payload.value.currentRole);

interface RoleOption { value: Role; name: string; badge: string; desc: string; danger?: boolean }
const ROLES: RoleOption[] = [
  { value: 'viewer', name: 'Наблюдатель', badge: 'viewer', desc: 'Видит ссылки и метрики, но не может ничего менять.' },
  { value: 'member', name: 'Участник',     badge: 'member', desc: 'Создаёт и редактирует свои ссылки, видит общие кампании.' },
  { value: 'admin',  name: 'Администратор', badge: 'admin',  desc: 'Полный доступ: команда, биллинг, домены, API-ключи.' },
  { value: 'owner',  name: 'Владелец',      badge: 'owner',  desc: 'Передача владения. Текущий владелец станет admin.', danger: true },
];

function badgeVariant(b: string): 'default' | 'success' | 'warn' | 'danger' {
  if (b === 'owner') return 'danger';
  if (b === 'admin') return 'warn';
  if (b === 'member') return 'success';
  return 'default';
}

const submitting = computed(() => modals.pending);
const isTransferOwnership = computed(() => newRole.value === 'owner' && payload.value.currentRole !== 'owner');
const hasChange = computed(() => newRole.value !== payload.value.currentRole);

function apply() {
  if (!hasChange.value) return;
  modals.setPending(true);
  modals.emit('change-role', { userId: payload.value.userId, newRole: newRole.value });
  modals.setPending(false);
  modals.close();
}
</script>

<template>
  <UiModal title="Изменить роль" size="sm" :disable-close="submitting" @close="modals.close">
    <!-- User card -->
    <div class="user-card">
      <div class="user-avatar">{{ payload.userInitials }}</div>
      <div class="user-info">
        <div class="user-name">{{ payload.userName }}</div>
        <div class="user-email">{{ payload.userEmail }}</div>
        <div class="user-meta">
          В команде с <strong>{{ payload.joinedAt }}</strong> · {{ payload.linksCount }} ссылок · {{ payload.campaignsCount }} кампаний
        </div>
      </div>
      <UiBadge :variant="badgeVariant(payload.currentRole)">{{ payload.currentRole }}</UiBadge>
    </div>

    <!-- Role selector -->
    <div class="role-section">
      <label class="field-label">Новая роль</label>
      <div class="role-grid" role="radiogroup" aria-label="Роль">
        <label
          v-for="r in ROLES"
          :key="r.value"
          :class="['role-card', { selected: newRole === r.value, current: payload.currentRole === r.value, danger: r.danger }]"
        >
          <input type="radio" v-model="newRole" :value="r.value" />
          <div class="role-body">
            <div class="role-head">
              <span class="role-name">{{ r.name }}</span>
              <UiBadge :variant="badgeVariant(r.badge)">{{ r.badge }}</UiBadge>
              <span v-if="payload.currentRole === r.value" class="current-marker">текущая</span>
            </div>
            <div class="role-desc">{{ r.desc }}</div>
          </div>
        </label>
      </div>
    </div>

    <div v-if="isTransferOwnership" class="transfer-warn">
      <UiBadge variant="danger" dot>Передача владения</UiBadge>
      <p>Вы перестанете быть владельцем команды. Передача невозможна без подтверждения второго фактора.</p>
    </div>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="modals.close">Отмена</UiButton>
      <UiButton
        :variant="isTransferOwnership ? 'danger' : 'primary'"
        :disabled="submitting || !hasChange"
        @click="apply"
      >
        {{ submitting ? 'Применяем…' : (isTransferOwnership ? 'Передать владение' : 'Применить') }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.user-card {
  display: flex; align-items: center; gap: 12px; padding: 12px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 10px; margin-bottom: 16px;
}
.user-avatar {
  width: 44px; height: 44px; border-radius: 10px;
  background: linear-gradient(135deg, #4f8cff, #7c5cff);
  color: #fff; display: grid; place-items: center;
  font-weight: 700; font-size: 14px; flex-shrink: 0;
}
.user-info { flex: 1; min-width: 0; }
.user-name { font-size: 14px; font-weight: 600; color: var(--fg, #e6eaf0); }
.user-email { font-size: 12px; color: var(--fg-muted, #9aa5b1); }
.user-meta { font-size: 11.5px; color: var(--fg-dim, #6b7480); margin-top: 4px; }
.user-meta strong { color: var(--fg, #e6eaf0); font-weight: 600; }

.role-section { margin-bottom: 14px; }
.field-label { display: block; font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); margin-bottom: 8px; }
.role-grid { display: flex; flex-direction: column; gap: 6px; }
.role-card {
  display: flex; gap: 10px; padding: 10px 12px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px; cursor: pointer; transition: 0.15s;
  align-items: flex-start;
}
.role-card input { position: absolute; opacity: 0; pointer-events: none; }
.role-card.selected { border-color: var(--accent, #4f8cff); box-shadow: 0 0 0 1px rgba(79,140,255,0.3); }
.role-card.danger.selected { border-color: #ff5d8f; box-shadow: 0 0 0 1px rgba(255,93,143,0.3); }
.role-card.current { background: rgba(79,140,255,0.04); }
.role-body { flex: 1; }
.role-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; }
.role-name { font-size: 13.5px; font-weight: 600; color: var(--fg, #e6eaf0); }
.current-marker {
  font-size: 10.5px; padding: 1px 7px; border-radius: 10px;
  background: var(--bg, #0f1620); color: var(--fg-muted, #9aa5b1);
}
.role-desc { font-size: 12px; color: var(--fg-muted, #9aa5b1); line-height: 1.45; }

.transfer-warn {
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px; background: rgba(255,93,143,0.08);
  border: 1px solid rgba(255,93,143,0.3); border-radius: 8px;
}
.transfer-warn p { font-size: 12.5px; color: var(--fg, #e6eaf0); margin: 0; line-height: 1.5; }
</style>
